import React, { useCallback, useEffect, useState } from "react";
import { useApi } from "../hooks/useApi";
import { formatUtcDateTime } from "../utils/dateTime";

/** Phase 3 identity sources, as the sync state names them. */
const SOURCE_NAMES = { Entra: "Entra ID", DefenderXdrIdentityInfo: "Defender XDR IdentityInfo", Okta: "Okta", Nhi: "Service principals (NHI)", KnowBe4: "KnowBe4", EntraAppConsents: "App consents", MdeMachines: "Devices (Defender for Endpoint)", DefenderXdrDeviceInfo: "Devices (Defender XDR DeviceInfo)", DefenderXdrDeviceLogons: "Device logon edges", ExposureGraphPosture: "Posture weaknesses (Exposure Management)", DefenderXdrIdentityActivity: "Identity activity (30 days)" };

/**
 * Defender for Identity (Phase 1) — the inventory sync panel under the Connector Settings
 * toggle on the Integrations page. Owns its own fetch / test / sync state so IntegrationSettings
 * only carries the toggle and the save hook.
 *
 * Wording rule: the rows come from the Defender XDR IdentityInfo table, which Entra ID populates
 * as well as MDI sensors, so nothing here claims "identities from Defender for Identity" — the
 * source is named as what it is.
 *
 * @param enabled        the SAVED toggle value (the panel only renders when the inventory is on)
 * @param canManage      canManageIntegrations — hides Sync / Test for read-only users
 * @param refreshToken   bump to re-fetch config (the parent does this after Save)
 * @param unavailable    the defenderIdentity module is unavailable on this installation —
 *                       Sync / Test are disabled (the parent shows the reason)
 */
export default function DefenderIdentityCard({ enabled, canManage, refreshToken, unavailable = false }) {
  const apiCall = useApi();
  const [config, setConfig] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [error, setError] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      const data = await apiCall("GET", "/defender-identity/config");
      setConfig(data);
    } catch { /* non-critical: the card degrades to "not synced yet" */ }
  }, [apiCall]);

  useEffect(() => { if (enabled) fetchConfig(); }, [enabled, fetchConfig, refreshToken]);

  const handleTest = useCallback(async () => {
    setTesting(true); setTestResult(null);
    try {
      setTestResult(await apiCall("POST", "/defender-identity/test-connection"));
    } catch (err) {
      setTestResult({ success: false, message: err?.response?.data?.message || "Test failed.", reason: "other" });
    } finally { setTesting(false); }
  }, [apiCall]);

  // A full run reads Entra, the XDR tables, app consents, devices, the exposure graph and the
  // activity tables — minutes, not seconds. So the button stays in its running state until the
  // server says the run ended, and the counts are only re-read then. A single delayed refresh (the
  // old behaviour) showed the PREVIOUS run's date and never corrected itself.
  const waitForSync = useCallback(async () => {
    const startedAt = Date.now();
    const deadline = 15 * 60 * 1000;   // a run that outlives this is reported, not waited on
    while (Date.now() - startedAt < deadline) {
      await new Promise(r => setTimeout(r, 5000));
      let status;
      try {
        // suppressAuthRedirect: a background poll must never bounce the user through re-login.
        status = await apiCall("GET", "/defender-identity/sync-status", null, false, null, true);
      } catch { continue; }          // a blip mid-run is not a failed sync
      if (status?.status === "Running") continue;
      await fetchConfig();
      if (status?.status === "Failed") setError(status.errorMessage || "The identity inventory sync failed.");
      else setSyncMessage("Sync complete.");
      return;
    }
    await fetchConfig();
    setSyncMessage("The sync is taking longer than expected — it is still running on the server.");
  }, [apiCall, fetchConfig]);

  const handleSync = useCallback(async () => {
    setSyncing(true); setError(null); setSyncMessage(null);
    let started = false;
    try {
      // 202 either way: `started` says whether a run began or the cooldown refused it.
      const res = await apiCall("POST", "/defender-identity/sync");
      setSyncMessage(res?.message || (res?.started ? "Sync started." : "Sync not started."));
      started = !!res?.started;
      if (started) await waitForSync();
    } catch (err) {
      setError(err?.response?.data?.message || "Sync request failed.");
    } finally { setSyncing(false); }
  }, [apiCall, waitForSync]);

  if (!enabled) return null;

  const last = config?.lastSync || null;
  const cooldownActive = last?.cooldownUntil && new Date(last.cooldownUntil) > new Date();

  return (
    <div className="mt-1 p-2 bg-gray-50 border border-gray-100 rounded-lg space-y-1.5" data-testid="defender-identity-card">
      {canManage && (
        <div className="flex items-center gap-2">
          <button type="button" onClick={handleSync} disabled={syncing || !!cooldownActive || unavailable}
            title={cooldownActive ? `Available again at ${formatUtcDateTime(last.cooldownUntil)}` : undefined}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors">
            {syncing
              ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Syncing…</>
              : "Sync Now"}
          </button>
          <button type="button" onClick={handleTest} disabled={testing || unavailable}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-200 disabled:opacity-50 transition-colors">
            {testing
              ? <><span className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />Testing…</>
              : "Test Connection"}
          </button>
        </div>
      )}

      <div className="text-[11px] text-gray-500">
        {config?.lastSyncAt
          ? `Last successful sync: ${formatUtcDateTime(config.lastSyncAt)}`
          : last && !last.success
            ? "No successful sync yet."
            : "Not synced yet — syncs nightly, or use Sync Now."}
      </div>

      {last && last.success && (
        <div className="text-[11px] text-gray-500">
          {last.fetched} identities read · {last.inserted} new · {last.updated} updated
          {last.reKeyed > 0 ? ` · ${last.reKeyed} re-keyed` : ""}
          {last.markedUnseen > 0 ? ` · ${last.markedUnseen} no longer reported` : ""}
        </div>
      )}
      {last && !last.success && last.error && (
        <div className="text-[11px] text-red-700">Last sync failed: {last.error}</div>
      )}
      {last?.sources && Object.keys(last.sources).length > 0 && (
        <ul className="text-[11px] text-gray-500 space-y-0.5" data-testid="identity-sources">
          {Object.entries(last.sources).map(([name, o]) => (
            <li key={name}>
              <span className="font-medium text-gray-700">{SOURCE_NAMES[name] || name}</span>
              {": "}
              {!o.ran ? "did not run"
                : o.success ? `${o.fetched} read · ${o.inserted} new · ${o.updated} updated${o.markedUnseen > 0 ? ` · ${o.markedUnseen} no longer reported` : ""}${o.truncated ? " · truncated" : ""}${name === "Entra" ? ` · ${o.rolesRead} directory roles${o.signInActivityAvailable === false ? " · sign-in activity not consented (AuditLog.Read.All)" : ""}` : ""}${o.error ? ` · ${o.error}` : ""}`
                : `failed — ${o.error || "see the server log"}`}
            </li>
          ))}
        </ul>
      )}
      {last?.truncated && (
        <div className="text-[11px] text-amber-700">
          The last pull hit the Advanced Hunting row cap (100,000), so this inventory is the first
          100,000 identities by key, not the whole directory. Nothing was marked as no longer
          reported.
        </div>
      )}
      {last && last.success && !last.lastActivityCollected && (
        <div className="text-[11px] text-gray-400">
          Last-activity lookup did not run on the last sync{last.lastActivityError ? ` (${last.lastActivityError})` : ""}; activity dates are from the previous run.
        </div>
      )}
      {cooldownActive && (
        <div className="text-[11px] text-gray-400">
          Sync Now is available again at {formatUtcDateTime(last.cooldownUntil)} (rate-limit guard for the tenant's Advanced Hunting quota).
        </div>
      )}

      {testResult && (
        <div className={`text-[11px] ${testResult.success ? "text-green-700" : "text-red-700"}`}>
          {testResult.message}
          {!testResult.success && testResult.reason === "consent" && (
            <span className="block text-gray-500 mt-0.5">
              The Axaris app registration needs the <code>AdvancedHunting.Read.All</code> application
              permission (Microsoft Threat Protection API), and admin consent must be re-granted in this tenant.
            </span>
          )}
        </div>
      )}
      {syncMessage && <div className="text-[11px] text-gray-600">{syncMessage}</div>}
      {error && <div className="text-[11px] text-red-700">{error}</div>}
    </div>
  );
}
